import { type ReactNode, useMemo, useState } from "react";
import { SearchInput } from "@/shared/components/ui/search-input";

import { Button } from "@/shared/components/ui/button";

import { cn } from "@/shared/lib/utils";

interface FilterableDetailListProps<T> {
  items: T[];
  getItemKey: (item: T, index: number) => string | number;
  getSearchText: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  renderList?: (args: {
    items: T[];
    getItemKey: (item: T, index: number) => string | number;
    renderItem: (item: T, index: number) => ReactNode;
    getItemOnClick?: (item: T) => (() => void) | undefined;
  }) => ReactNode;
  getItemOnClick?: (item: T) => (() => void) | undefined;
  itemLabel?: string;
  pageSize?: number;
  scrollable?: boolean;
  /** Tailwind max-height class applied when scrollable, e.g. "max-h-[300px]" */
  maxHeight?: string; // Tailwind class, e.g. "max-h-[300px]"
  searchPlaceholder?: string;
  emptyState?: ReactNode;
  emptyFilteredState?: ReactNode;
  className?: string;
  headerClassName?: string;
  paginationClassName?: string;
}

export function FilterableDetailList<T>({
  items,
  getItemKey,
  getSearchText,
  renderItem,
  renderList,
  getItemOnClick,
  itemLabel = "items",
  pageSize = 6,
  scrollable = false,
  maxHeight = "max-h-[300px]",
  searchPlaceholder = "Search",
  emptyState,
  emptyFilteredState,
  className,
  headerClassName,
  paginationClassName,
}: FilterableDetailListProps<T>) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const normalizedQuery = query.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) return items;

    return items.filter((item) =>
      getSearchText(item).toLowerCase().includes(normalizedQuery),
    );
  }, [getSearchText, items, normalizedQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pagedItems = scrollable
    ? filteredItems
    : filteredItems.slice(startIndex, startIndex + pageSize);
  const hasQuery = normalizedQuery.length > 0;
  const showingFrom = filteredItems.length === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + pageSize, filteredItems.length);

  return (
    <div className={className}>
      <div className={cn("mb-3", headerClassName)}>
        <SearchInput
          value={query}
          onChange={(v) => {
            setQuery(v);
            setPage(1);
          }}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="h-9 border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[13px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus-visible:border-[var(--green)]"
        />

        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
            {hasQuery
              ? `${filteredItems.length} matching ${itemLabel}`
              : `${items.length} ${itemLabel}`}
          </p>
          {!scrollable && filteredItems.length > 0 ? (
            <p className="text-[11px] text-[var(--ink-3)]">
              Showing {showingFrom}-{showingTo}
            </p>
          ) : null}
        </div>
      </div>

      <div
        className={cn(scrollable && "overflow-y-auto", scrollable && maxHeight)}
      >
        {pagedItems.length > 0 ? (
          renderList ? (
            renderList({
              items: pagedItems,
              getItemKey,
              renderItem: (item) => renderItem(item),
              getItemOnClick,
            })
          ) : (
            <div>
              {pagedItems.map((item, index) => {
                const onClick = getItemOnClick?.(item);
                const sharedClassName =
                  "w-full border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-3 text-left last:border-b-0";

                return onClick ? (
                  <Button
                    key={getItemKey(item, index)}
                    type="button"
                    variant="ghost"
                    onClick={onClick}
                    className={cn(
                      sharedClassName,
                      "h-auto cursor-pointer justify-start rounded-none border-none shadow-none",
                      "transition-colors hover:bg-[var(--bg-subtle)]",
                    )}
                  >
                    {renderItem(item)}
                  </Button>
                ) : (
                  <div
                    key={getItemKey(item, index)}
                    className={cn(
                      sharedClassName,
                      index === pagedItems.length - 1 && totalPages === 1
                        ? "border-b-0"
                        : "",
                    )}
                  >
                    {renderItem(item)}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="px-4 py-6 text-[12px] text-[var(--ink-3)]">
            {hasQuery
              ? (emptyFilteredState ?? "No matching items found.")
              : (emptyState ?? "No items available.")}
          </div>
        )}
      </div>

      {!scrollable && filteredItems.length > pageSize ? (
        <div className={cn("mt-3 flex items-center justify-between gap-3", paginationClassName)}>
          <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() =>
                setPage((currentValue) => Math.max(1, currentValue - 1))
              }
              disabled={currentPage <= 1}
              className="h-7 rounded-[6px] border-[0.5px] border-[var(--rule)] px-2.5 text-[11px] text-[var(--ink-3)]"
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() =>
                setPage((currentValue) => Math.min(totalPages, currentValue + 1))
              }
              disabled={currentPage >= totalPages}
              className="h-7 rounded-[6px] border-[0.5px] border-[var(--rule)] px-2.5 text-[11px] text-[var(--ink-3)]"
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
